import classNames from 'classnames';
import { useId, type PropsWithChildren } from 'react';
import styled, { keyframes } from 'styled-components';

import { Horizontal } from '@sorare/core/src/atoms/layout/flex';

const floatingKeyframes = keyframes`
 0% {
  transform: translateY(0%);
 }
 50% {
  transform: translateY(-20%);
 }
 100% {
  transform: translateY(0%);
 }
`;
const ArrowShapeWrapper = styled.div`
  position: absolute;
  bottom: 64%;

  &.floating {
    animation: ${floatingKeyframes} 2s ease-in-out infinite;
  }

  button:hover > &.animatedOnHover,
  a:hover > &.animatedOnHover {
    animation: ${floatingKeyframes} 2s ease-in-out infinite;
  }
`;
const Children = styled(Horizontal).attrs({ gap: 0, center: true })`
  position: absolute;
  width: 100%;
  left: 0;
  right: 0;
  top: 0;
  bottom: 12%;
`;

type Props = {
  floating?: boolean;
  animatedOnHover?: boolean;
  colors?: { start: string; end: string };
};
export const ArrowDownShape = ({
  floating,
  animatedOnHover,
  colors,
  children,
}: PropsWithChildren<Props>) => {
  const gradientId = useId();
  const filterId = useId();
  return (
    <ArrowShapeWrapper className={classNames({ floating, animatedOnHover })}>
      <div style={{ position: 'relative' }}>
        <svg
          width="70"
          height="70"
          viewBox="0 0 70 70"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <g filter={`url(#${filterId})`}>
            <path
              d="M0 47.0831V4C0 1.79086 1.79086 0 4 0H65C67.2091 0 69 1.79086 69 4V47.1486C69 48.5464 68.2703 49.8429 67.0754 50.5681L37.4912 68.5234C36.2462 69.279 34.6889 69.2982 33.4256 68.5736L2.00972 50.5528C0.766639 49.8398 0 48.5162 0 47.0831Z"
              fill={`url(#${gradientId})`}
            />
          </g>
          <defs>
            <filter
              id={filterId}
              x="0"
              y="0"
              width="69"
              height="71.3242"
              filterUnits="userSpaceOnUse"
              colorInterpolationFilters="sRGB"
            >
              <feFlood floodOpacity="0" result="BackgroundImageFix" />
              <feBlend
                mode="normal"
                in="SourceGraphic"
                in2="BackgroundImageFix"
                result="shape"
              />
              <feColorMatrix
                in="SourceAlpha"
                type="matrix"
                values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
                result="hardAlpha"
              />
              <feOffset dy="2.95127" />
              <feGaussianBlur stdDeviation="1.11015" />
              <feComposite
                in2="hardAlpha"
                operator="arithmetic"
                k2="-1"
                k3="1"
              />
              <feColorMatrix
                type="matrix"
                values="0 0 0 0 1 0 0 0 0 1 0 0 0 0 1 0 0 0 0.75 0"
              />
              <feBlend
                mode="normal"
                in2="shape"
                result="effect1_innerShadow_1_5"
              />
            </filter>
            <radialGradient
              id={gradientId}
              cx="0"
              cy="0"
              r="1"
              gradientUnits="userSpaceOnUse"
              gradientTransform="translate(33.6288 47.1499) scale(35.3712 87.9522)"
            >
              <stop stopColor={colors?.start || '#1B034B'} />
              <stop offset="1" stopColor={colors?.end || '#7029FF'} />
            </radialGradient>
          </defs>
        </svg>
        {!!children && <Children>{children}</Children>}
      </div>
    </ArrowShapeWrapper>
  );
};
